import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Users,
  DollarSign,
  TrendingUp,
  Package,
  Bell,
  Send,
  CheckCircle,
  XCircle,
  Activity,
  Building2,
  UserPlus,
  Shield,
  Sparkles,
  ListChecks,
  PenLine,
  Trash2,
  ShoppingCart,
  Eye,
  X,
  Loader2
} from 'lucide-react';
import {
  getSuperAdminStats,
  getAllCompanies,
  getCompanyOrders,
  getCompanyDetails,
  getCompanyClients,
  updateCompanyBySuperAdmin,
  deleteCompanyBySuperAdmin,
  sendGlobalNotification,
  getSentNotifications,
  createSuperAdmin,
  fetchAIBusinessIdeas,
  createAIBusinessIdea,
  updateAIBusinessIdea,
  deleteAIBusinessIdea,
  getAllCommissions,
  updateCommission
} from '../../../redux/api';
import { formatInvoiceEur } from '../../../utils/invoiceDisplayFormat';
import { formatBillingYmdLocal } from '../../../utils/billingDateFormat';
import { packageLabelSr, packageBadgeClass, resolveCompanyPackageNumber } from '../../../utils/packageDisplay';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [sentNotifications, setSentNotifications] = useState([]);
  const [showNotificationForm, setShowNotificationForm] = useState(false);
  const [showCreateAdminForm, setShowCreateAdminForm] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    type: 'info',
    priority: 'medium',
    targetAudience: 'all'
  });
  const [adminForm, setAdminForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    ownerName: '',
    companyName: '',
    phone: '',
    city: '',
    address: '',
    businessType: 'Home Appliance Technician'
  });
  const [aiIdeas, setAIIdeas] = useState([]);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [savingIdea, setSavingIdea] = useState(false);
  const [editingIdeaId, setEditingIdeaId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyOrders, setCompanyOrders] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [commissions, setCommissions] = useState([]);
  const [commissionsLoading, setCommissionsLoading] = useState(false);
  const [commissionStats, setCommissionStats] = useState(null);
  const [commissionFilters, setCommissionFilters] = useState({
    status: '',
    companyId: '',
    startDate: '',
    endDate: ''
  });
  const [showCommissions, setShowCommissions] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [loadingCompanyDetails, setLoadingCompanyDetails] = useState(false);
  const [viewingClients, setViewingClients] = useState(null);
  const [companyClients, setCompanyClients] = useState(null);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsPage, setClientsPage] = useState(1);
  const [ideaForm, setIdeaForm] = useState({
    title: '',
    phase: 'Strategija',
    summary: '',
    actionsText: '',
    aiAssist: '',
    impact: '',
    resources: '',
    tags: '',
    targetClientCount: '',
  });
  const phaseOptions = [
    'Strategija',
    'Marketinške aktivnosti',
    'Prodaja',
    'Operativa',
    'Automatizacija',
    'Analitika',
    'Ostalo',
  ];

  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.result?.role === 'superadmin';

  const refreshIdeas = async (showSpinner = true) => {
    try {
      if (showSpinner) {
        setIdeasLoading(true);
      }
      const ideasResponse = await fetchAIBusinessIdeas();
      setAIIdeas(Array.isArray(ideasResponse?.data) ? ideasResponse.data : []);
    } catch (error) {
      console.error('Error fetching AI business ideas:', error);
      toast.error('Greška pri učitavanju AI biznis ideja');
    } finally {
      if (showSpinner) {
        setIdeasLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error('Nemate pristup ovoj stranici');
      return;
    }

    fetchData();
  }, [isSuperAdmin]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsResponse, notificationsResponse, ideasResponse] = await Promise.all([
        getSuperAdminStats(),
        getSentNotifications(),
        fetchAIBusinessIdeas().catch(() => ({ data: [] }))
      ]);

      setStats(statsResponse.data);
      setSentNotifications(notificationsResponse.data || []);
      setAIIdeas(Array.isArray(ideasResponse?.data) ? ideasResponse.data : []);
    } catch (error) {
      console.error('Error fetching super admin data:', error);
      toast.error('Greška pri učitavanju podataka');
    } finally {
      setLoading(false);
      setIdeasLoading(false);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!notificationForm.title || !notificationForm.message) {
      toast.error('Naslov i poruka su obavezni');
      return;
    }

    try {
      setSendingNotification(true);
      await sendGlobalNotification(notificationForm);
      toast.success('Obaveštenje je uspešno poslato');
      setNotificationForm({
        title: '',
        message: '',
        type: 'info',
        priority: 'medium',
        targetAudience: 'all'
      });
      setShowNotificationForm(false);
      await fetchData();
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error(error.response?.data?.message || 'Greška pri slanju obaveštenja');
    } finally {
      setSendingNotification(false);
    }
  };

  const handleChange = (e) => {
    setNotificationForm({
      ...notificationForm,
      [e.target.name]: e.target.value
    });
  };

  const handleAdminFormChange = (e) => {
    setAdminForm({
      ...adminForm,
      [e.target.name]: e.target.value
    });
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (!adminForm.email || !adminForm.password) {
      toast.error('Email i lozinka su obavezni');
      return;
    }

    if (adminForm.password.length < 6) {
      toast.error('Lozinka mora imati najmanje 6 karaktera');
      return;
    }

    if (adminForm.password !== adminForm.confirmPassword) {
      toast.error('Lozinke se ne poklapaju');
      return;
    }

    try {
      setCreatingAdmin(true);
      const { confirmPassword, ...adminData } = adminForm;
      await createSuperAdmin(adminData);
      toast.success('Super admin nalog je uspešno kreiran');
      setAdminForm({
        email: '',
        password: '',
        confirmPassword: '',
        ownerName: '',
        companyName: '',
        phone: '',
        city: '',
        address: '',
        businessType: 'Home Appliance Technician'
      });
      setShowCreateAdminForm(false);
      await fetchData();
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error(error.response?.data?.message || 'Greška pri kreiranju naloga');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const resetIdeaForm = () => {
    setIdeaForm({
      title: '',
      phase: 'Strategija',
      summary: '',
      actionsText: '',
      aiAssist: '',
      impact: '',
      resources: '',
      tags: '',
      targetClientCount: '',
    });
    setEditingIdeaId(null);
  };

  const handleIdeaFormChange = (event) => {
    const { name, value } = event.target;
    setIdeaForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleIdeaEdit = (idea) => {
    setIdeaForm({
      title: idea.title || '',
      phase: idea.phase || 'Strategija',
      summary: idea.summary || '',
      actionsText: Array.isArray(idea.actionSteps) ? idea.actionSteps.join('\n') : '',
      aiAssist: idea.aiAssist || '',
      impact: idea.impact || '',
      targetClientCount: idea.targetClientCount || '',
      resources: idea.resources || '',
      tags: Array.isArray(idea.tags) ? idea.tags.join(', ') : '',
    });
    setEditingIdeaId(idea.id);
  };

  const handleIdeaDelete = async (id) => {
    const confirmDelete = window.confirm('Da li si siguran da želiš da obrišeš ovu ideju?');
    if (!confirmDelete) return;

    try {
      await deleteAIBusinessIdea(id);
      toast.success('AI biznis ideja je obrisana.');
      await refreshIdeas();
    } catch (error) {
      console.error('Error deleting AI business idea:', error);
      toast.error(error.response?.data?.message || 'Greška pri brisanju ideje.');
    }
  };

  const handleIdeaSubmit = async (event) => {
    event.preventDefault();
    if (!ideaForm.title.trim()) {
      toast.error('Naslov ideje je obavezan.');
      return;
    }
    if (!ideaForm.phase.trim()) {
      toast.error('Faza poslovanja je obavezna.');
      return;
    }

    const payload = {
      title: ideaForm.title.trim(),
      phase: ideaForm.phase.trim(),
      summary: ideaForm.summary.trim(),
      actionSteps: ideaForm.actionsText
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
      aiAssist: ideaForm.aiAssist.trim(),
      impact: ideaForm.impact.trim(),
      resources: ideaForm.resources.trim(),
      tags: ideaForm.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
      targetClientCount: ideaForm.targetClientCount 
        ? Number(ideaForm.targetClientCount) 
        : null,
    };

    try {
      setSavingIdea(true);
      if (editingIdeaId) {
        await updateAIBusinessIdea({ id: editingIdeaId, ideaData: payload });
        toast.success('AI biznis ideja je ažurirana.');
      } else {
        await createAIBusinessIdea(payload);
        toast.success('AI biznis ideja je dodata.');
      }
      await refreshIdeas();
      resetIdeaForm();
    } catch (error) {
      console.error('Error saving AI business idea:', error);
      toast.error(error.response?.data?.message || 'Greška pri čuvanju ideje.');
    } finally {
      setSavingIdea(false);
    }
  };

  const groupedIdeas = useMemo(() => {
    if (!Array.isArray(aiIdeas) || aiIdeas.length === 0) return [];
    const phaseMap = aiIdeas.reduce((acc, idea) => {
      const phase = idea.phase || 'Druge faze';
      if (!acc[phase]) {
        acc[phase] = [];
      }
      acc[phase].push(idea);
      return acc;
    }, {});

    return Object.entries(phaseMap)
      .map(([phase, items]) => ({
        phase,
        items: items.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
      }))
      .sort((a, b) => {
        const phaseA = a.phase || '';
        const phaseB = b.phase || '';
        return phaseA.localeCompare(phaseB, 'sr', { sensitivity: 'base' });
      });
  }, [aiIdeas]);

  const fetchCommissions = async () => {
    try {
      setCommissionsLoading(true);
      const params = {};
      if (commissionFilters.status) params.status = commissionFilters.status;
      if (commissionFilters.companyId) params.companyId = commissionFilters.companyId;
      if (commissionFilters.startDate) params.startDate = commissionFilters.startDate;
      if (commissionFilters.endDate) params.endDate = commissionFilters.endDate;

      const response = await getAllCommissions(params);
      setCommissions(response.data.commissions || []);
      setCommissionStats(response.data.stats || null);
    } catch (error) {
      console.error('Error fetching commissions:', error);
      toast.error(error.response?.data?.message || 'Greška pri učitavanju provizija');
    } finally {
      setCommissionsLoading(false);
    }
  };

  const handleUpdateCommissionStatus = async (commissionId, newStatus, paymentReference = '') => {
    try {
      await updateCommission(commissionId, {
        status: newStatus,
        paymentReference: paymentReference || undefined
      });
      toast.success('Status provizije je ažuriran');
      await fetchCommissions();
      setEditingCommission(null);
    } catch (error) {
      console.error('Error updating commission:', error);
      toast.error(error.response?.data?.message || 'Greška pri ažuriranju provizije');
    }
  };

  useEffect(() => {
    if (showCommissions) {
      fetchCommissions();
      if (companies.length === 0) {
        getAllCompanies()
          .then((response) => setCompanies(response.data || []))
          .catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCommissions, commissionFilters.status, commissionFilters.companyId, commissionFilters.startDate, commissionFilters.endDate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sr-RS', {
      style: 'currency',
      currency: 'RSD'
    }).format(amount || 0);
  };

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Nemate pristup ovoj stranici</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Super Admin Dashboard
          </h1>
          <p className="text-gray-400">Pregled svih korisnika i preplata</p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <Users className="h-8 w-8 text-blue-500" />
            </div>
            <h3 className="text-gray-400 text-sm mb-1">Ukupno kompanija</h3>
            <p className="text-2xl font-bold text-white">{stats?.totalCompanies || 0}</p>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="text-gray-400 text-sm mb-1">Sa preplatama</h3>
            <p className="text-2xl font-bold text-white">{stats?.companiesWithSubscription || 0}</p>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <Users className="h-8 w-8 text-purple-500" />
            </div>
            <h3 className="text-gray-400 text-sm mb-1">Ukupno klijenata</h3>
            <p className="text-2xl font-bold text-white">{stats?.totalClients || 0}</p>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <Package className="h-8 w-8 text-blue-500" />
            </div>
            <h3 className="text-gray-400 text-sm mb-1">Poslovi</h3>
            <p className="text-2xl font-bold text-white">{stats?.totalCompletedJobs || 0}</p>
          </div>
        </div>

        {/* Additional Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <XCircle className="h-8 w-8 text-yellow-500" />
            </div>
            <h3 className="text-gray-400 text-sm mb-1">Bez preplata</h3>
            <p className="text-2xl font-bold text-white">{stats?.companiesWithoutSubscription || 0}</p>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="text-gray-400 text-sm mb-1">Ukupan prihod</h3>
            <p className="text-2xl font-bold text-white">{formatCurrency(stats?.totalRevenue || 0)}</p>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="h-8 w-8 text-cyan-500" />
            </div>
            <h3 className="text-gray-400 text-sm mb-1">Prosečno klijenata/kompaniji</h3>
            <p className="text-2xl font-bold text-white">
              {stats?.totalCompanies > 0 
                ? Math.round((stats?.totalClients || 0) / stats.totalCompanies) 
                : 0}
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <Activity className="h-8 w-8 text-orange-500" />
            </div>
            <h3 className="text-gray-400 text-sm mb-1">Prosečno poslova/kompaniji</h3>
            <p className="text-2xl font-bold text-white">
              {stats?.totalCompanies > 0 
                ? Math.round((stats?.totalCompletedJobs || 0) / stats.totalCompanies) 
                : 0}
            </p>
          </div>
        </div>

        {/* Package Breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-gray-400 text-sm mb-2">Besplatni</h3>
            <p className="text-3xl font-bold text-white">{stats?.packageBreakdown?.free || 0}</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-gray-400 text-sm mb-2">Starter</h3>
            <p className="text-3xl font-bold text-sky-400">{stats?.packageBreakdown?.starter || 0}</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-gray-400 text-sm mb-2">Standardni</h3>
            <p className="text-3xl font-bold text-blue-500">{stats?.packageBreakdown?.standard || 0}</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-gray-400 text-sm mb-2">Biznis</h3>
            <p className="text-3xl font-bold text-purple-500">{stats?.packageBreakdown?.business || 0}</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-gray-400 text-sm mb-2">Premium</h3>
            <p className="text-3xl font-bold text-yellow-500">{stats?.packageBreakdown?.premium || 0}</p>
          </div>
        </div>

        {/* Create Super Admin Section */}
        <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-500" />
              <h2 className="text-xl font-bold text-white">Kreiranje Super Admin naloga</h2>
            </div>
            <button
              onClick={() => setShowCreateAdminForm(!showCreateAdminForm)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <UserPlus className="h-4 w-4" />
              {showCreateAdminForm ? 'Otkaži' : 'Kreiraj Super Admin'}
            </button>
          </div>

          {showCreateAdminForm && (
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={adminForm.email}
                    onChange={handleAdminFormChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Ime vlasnika
                  </label>
                  <input
                    type="text"
                    name="ownerName"
                    value={adminForm.ownerName}
                    onChange={handleAdminFormChange}
                    placeholder="Super Admin"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={adminForm.password}
                    onChange={handleAdminFormChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Potvrdi Password *
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={adminForm.confirmPassword}
                    onChange={handleAdminFormChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Ime kompanije
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={adminForm.companyName}
                    onChange={handleAdminFormChange}
                    placeholder="SpinTasker Admin"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={adminForm.phone}
                    onChange={handleAdminFormChange}
                    placeholder="+381601234567"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Grad
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={adminForm.city}
                    onChange={handleAdminFormChange}
                    placeholder="Beograd"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Adresa
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={adminForm.address}
                    onChange={handleAdminFormChange}
                    placeholder="N/A"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Tip biznisa
                  </label>
                  <select
                    name="businessType"
                    value={adminForm.businessType}
                    onChange={handleAdminFormChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="Home Appliance Technician">Home Appliance Technician</option>
                    <option value="Electrician">Electrician</option>
                    <option value="Plumber">Plumber</option>
                    <option value="Auto Mechanic">Auto Mechanic</option>
                    <option value="Elevator Technician">Elevator Technician</option>
                    <option value="HVAC Technician">HVAC Technician</option>
                    <option value="Carpenter">Carpenter</option>
                    <option value="Locksmith">Locksmith</option>
                    <option value="Tile Installer">Tile Installer</option>
                    <option value="Painter">Painter</option>
                    <option value="Facade Specialist">Facade Specialist</option>
                    <option value="IT Technician">IT Technician</option>
                    <option value="Handyman">Handyman</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={creatingAdmin}
                className="w-full sm:w-auto px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {creatingAdmin ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Kreiranje...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4" />
                    Kreiraj Super Admin nalog
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Send Notification Section */}
        <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-blue-500" />
              <h2 className="text-xl font-bold text-white">Slanje obaveštenja</h2>
            </div>
            <button
              onClick={() => setShowNotificationForm(!showNotificationForm)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              {showNotificationForm ? 'Otkaži' : 'Pošalji obaveštenje'}
            </button>
          </div>

          {showNotificationForm && (
            <form onSubmit={handleSendNotification} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Naslov
                </label>
                <input
                  type="text"
                  name="title"
                  value={notificationForm.title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Poruka
                </label>
                <textarea
                  name="message"
                  value={notificationForm.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Tip
                  </label>
                  <select
                    name="type"
                    value={notificationForm.type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="info">Info</option>
                    <option value="success">Uspeh</option>
                    <option value="warning">Upozorenje</option>
                    <option value="error">Greška</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Prioritet
                  </label>
                  <select
                    name="priority"
                    value={notificationForm.priority}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Nizak</option>
                    <option value="medium">Srednji</option>
                    <option value="high">Visok</option>
                    <option value="urgent">Hitno</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Ciljana grupa
                  </label>
                  <select
                    name="targetAudience"
                    value={notificationForm.targetAudience}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Svi korisnici</option>
                    <option value="subscribers">Preplatnici</option>
                    <option value="free">Besplatni paket</option>
                    <option value="standard">Standardni</option>
                    <option value="business">Biznis</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={sendingNotification}
                className="w-full sm:w-auto px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {sendingNotification ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Šalje se...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Pošalji obaveštenje
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* AI Business Ideas Management */}
        <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-6 border border-emerald-600/40 shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-emerald-400" />
              <div>
                <h2 className="text-xl font-bold text-white">AI Biznis ideje i faze</h2>
                <p className="text-sm text-emerald-100/80">
                  Definiši smernice po fazama i podeli ih sa svim kompanijama. AI asistencija je uključena u svaku ideju.
                </p>
              </div>
            </div>
            {editingIdeaId && (
              <button
                type="button"
                onClick={resetIdeaForm}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Otkaži izmenu
              </button>
            )}
          </div>

          <form onSubmit={handleIdeaSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Naslov ideje *
                </label>
                <input
                  type="text"
                  name="title"
                  value={ideaForm.title}
                  onChange={handleIdeaFormChange}
                  placeholder="npr. Servis veš mašina sa AI dijagnostikom"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Faza poslovanja *
                </label>
                <select
                  name="phase"
                  value={ideaForm.phase}
                  onChange={handleIdeaFormChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                >
                  {phaseOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Kratki opis / cilj
              </label>
              <textarea
                name="summary"
                value={ideaForm.summary}
                onChange={handleIdeaFormChange}
                rows={2}
                placeholder="Šta je ideja i kakav rezultat očekujemo?"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Koraci za tim (jedan po liniji)
                </label>
                <textarea
                  name="actionsText"
                  value={ideaForm.actionsText}
                  onChange={handleIdeaFormChange}
                  rows={4}
                  placeholder={`1. Analiziraj istoriju servisa\n2. Kreiraj AI ponudu sa cenom\n3. Zakazi termin za testiranje`}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    AI asistencija / alati
                  </label>
                  <textarea
                    name="aiAssist"
                    value={ideaForm.aiAssist}
                    onChange={handleIdeaFormChange}
                    rows={2}
                    placeholder="npr. ChatGPT za skripte, Make.com za automatizaciju e-maila"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Očekivani uticaj
                  </label>
                  <textarea
                    name="impact"
                    value={ideaForm.impact}
                    onChange={handleIdeaFormChange}
                    rows={2}
                    placeholder="npr. +20% upita u prvoj sedmici, manje vremena za ponude"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Resursi / linkovi
                </label>
                <textarea
                  name="resources"
                  value={ideaForm.resources}
                  onChange={handleIdeaFormChange}
                  rows={2}
                  placeholder="Link ka template-u ili treningu"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Tagovi (odvojeni zarezom)
                </label>
                <input
                  type="text"
                  name="tags"
                  value={ideaForm.tags}
                  onChange={handleIdeaFormChange}
                  placeholder="marketing, upsell, automatizacija"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Ciljani broj klijenata (opciono)
              </label>
              <input
                type="number"
                name="targetClientCount"
                value={ideaForm.targetClientCount}
                onChange={handleIdeaFormChange}
                placeholder="npr. 100 (za praćenje napretka)"
                min="0"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <p className="mt-1 text-xs text-gray-400">
                Ako je unet, prikazuje se razlomak (npr. 0/100) i automatski se označava kada se dostigne cilj
              </p>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                type="submit"
                disabled={savingIdea}
                className="inline-flex items-center justify-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-600 text-white rounded-lg transition-colors"
              >
                {savingIdea ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Čuvamo...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    {editingIdeaId ? 'Sačuvaj izmene' : 'Dodaj AI biznis ideju'}
                  </>
                )}
              </button>
              {!savingIdea && (
                <p className="text-xs text-gray-400">
                  AI smernice se odmah vide u sekciji „AI Biznis“ kod svih kompanija.
                </p>
              )}
            </div>
          </form>

          <div className="mt-6">
            <div className="flex items-center gap-2 mb-3">
              <ListChecks className="h-5 w-5 text-emerald-300" />
              <h3 className="text-lg font-semibold text-white">Postojeće ideje po fazama</h3>
            </div>

            {ideasLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-400"></div>
              </div>
            ) : groupedIdeas.length === 0 ? (
              <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4 text-sm text-gray-300">
                Još uvek nema sačuvanih ideja. Dodaj prvu ideju iznad i kompanije će odmah dobiti smernice.
              </div>
            ) : (
              <div className="space-y-4">
                {groupedIdeas.map(({ phase, items }) => (
                  <div key={phase} className="bg-gray-900/70 border border-gray-700 rounded-xl">
                    <div className="px-4 py-3 border-b border-gray-700 text-sm font-semibold text-emerald-200 uppercase tracking-wide">
                      {phase} ({items.length})
                    </div>
                    <div className="p-4 space-y-3">
                      {items.map((idea) => (
                        <div key={idea.id} className="bg-gray-800/80 border border-gray-700 rounded-xl p-4 space-y-3">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div>
                              <h4 className="text-lg font-semibold text-white">{idea.title}</h4>
                              {idea.summary && <p className="text-sm text-gray-300 mt-1">{idea.summary}</p>}
                              <p className="text-[11px] text-gray-500 mt-1">
                                Ažurirano: {idea.updatedAt ? new Date(idea.updatedAt).toLocaleString('sr-RS') : '—'}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleIdeaEdit(idea)}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-lg transition-colors"
                              >
                                <PenLine className="h-4 w-4" />
                                Izmeni
                              </button>
                              <button
                                type="button"
                                onClick={() => handleIdeaDelete(idea.id)}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                                Obriši
                              </button>
                            </div>
                          </div>

                          {idea.actionSteps?.length > 0 && (
                            <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
                              <p className="text-xs font-semibold uppercase text-gray-400 mb-2">Koraci</p>
                              <ul className="list-disc list-inside text-sm text-gray-200 space-y-1">
                                {idea.actionSteps.map((step, index) => (
                                  <li key={`${idea.id}-step-${index}`}>{step}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {(idea.aiAssist || idea.impact || idea.resources) && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-200">
                              {idea.aiAssist && (
                                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                                  <p className="uppercase tracking-wide text-blue-200 font-semibold mb-1">AI asistencija</p>
                                  <p className="leading-relaxed">{idea.aiAssist}</p>
                                </div>
                              )}
                              {idea.impact && (
                                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                                  <p className="uppercase tracking-wide text-purple-200 font-semibold mb-1">Uticaj</p>
                                  <p className="leading-relaxed">{idea.impact}</p>
                                </div>
                              )}
                              {idea.resources && (
                                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                                  <p className="uppercase tracking-wide text-emerald-200 font-semibold mb-1">Resursi</p>
                                  <p className="leading-relaxed">{idea.resources}</p>
                                </div>
                              )}
                            </div>
                          )}

                          {idea.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-2 text-[11px] text-gray-300">
                              {idea.tags.map((tag) => (
                                <span key={`${idea.id}-${tag}`} className="px-3 py-1 bg-gray-900 border border-gray-700 rounded-full">
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {idea.responsesSummary && (
                            <div className="bg-gray-900/60 border border-emerald-600/40 rounded-lg p-3 space-y-3">
                              <div className="flex flex-wrap items-center gap-4 text-sm">
                                <div className="inline-flex items-center gap-2 text-emerald-300">
                                  <CheckCircle className="h-4 w-4" />
                                  <span className="font-semibold">
                                    Pokreću: {idea.responsesSummary.acceptedCount}
                                  </span>
                                </div>
                                <div className="inline-flex items-center gap-2 text-red-300">
                                  <XCircle className="h-4 w-4" />
                                  <span className="font-semibold">
                                    Preskaču: {idea.responsesSummary.declinedCount}
                                  </span>
                                </div>
                              </div>

                              {(idea.responsesSummary.accepted.length > 0 ||
                                idea.responsesSummary.declined.length > 0) && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-200">
                                  {idea.responsesSummary.accepted.length > 0 && (
                                    <div>
                                      <p className="text-[11px] uppercase tracking-wide text-emerald-300 mb-2">
                                        Kompanije koje pokreću fazu
                                      </p>
                                      <ul className="space-y-1">
                                        {idea.responsesSummary.accepted.map((entry) => (
                                          <li
                                            key={`${idea.id}-accepted-${entry.companyId}`}
                                            className="flex items-center justify-between gap-2 bg-gray-800/70 border border-gray-700 rounded-lg px-3 py-2"
                                          >
                                            <span className="font-medium text-white">{entry.companyName}</span>
                                            <span className="text-[11px] text-gray-400">
                                              {entry.respondedAt
                                                ? new Date(entry.respondedAt).toLocaleString('sr-RS')
                                                : '—'}
                                            </span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {idea.responsesSummary.declined.length > 0 && (
                                    <div>
                                      <p className="text-[11px] uppercase tracking-wide text-red-300 mb-2">
                                        Kompanije koje preskaču
                                      </p>
                                      <ul className="space-y-1">
                                        {idea.responsesSummary.declined.map((entry) => (
                                          <li
                                            key={`${idea.id}-declined-${entry.companyId}`}
                                            className="flex items-center justify-between gap-2 bg-gray-800/70 border border-gray-700 rounded-lg px-3 py-2"
                                          >
                                            <span className="font-medium text-white">{entry.companyName}</span>
                                            <span className="text-[11px] text-gray-400">
                                              {entry.respondedAt
                                                ? new Date(entry.respondedAt).toLocaleString('sr-RS')
                                                : '—'}
                                            </span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {(stats?.unfinishedSignups || []).length > 0 && (
          <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-6 border border-amber-700/50">
            <div className="flex items-center gap-2 mb-3">
              <UserPlus className="h-5 w-5 text-amber-400" />
              <h2 className="text-xl font-bold text-white">Nedovršene registracije</h2>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Uneli su email na formi, ali nisu završili registraciju.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700 text-gray-400">
                    <th className="text-left py-2 px-2">Email</th>
                    <th className="text-left py-2 px-2">Kompanija</th>
                    <th className="text-left py-2 px-2">Poslednji unos</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.unfinishedSignups.map((row) => (
                    <tr key={row.email} className="border-b border-gray-700/70">
                      <td className="py-2 px-2 text-white">{row.email}</td>
                      <td className="py-2 px-2 text-gray-300">{row.companyName || '—'}</td>
                      <td className="py-2 px-2 text-gray-400">
                        {row.lastSeenAt ? new Date(row.lastSeenAt).toLocaleString('sr-RS') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-6 border border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-500" />
              <div>
                <h2 className="text-xl font-bold text-white">Kompanije</h2>
                <p className="text-sm text-gray-400">Lista, izmena i brisanje su u sekciji Lista kompanija.</p>
              </div>
            </div>
            <a
              href={window.location.pathname.replace(/superadmin\/?$/, 'kompanije')}
              className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm"
            >
              Otvori listu kompanija
            </a>
          </div>
        </div>

        {/* Companies List — moved to Lista kompanija */}
        {false && (
        <div className="bg-gray-800 rounded-lg p-4 sm:p-6 border border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5 text-blue-500" />
            <h2 className="text-xl font-bold text-white">Lista kompanija</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Kompanija</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Email</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Paket</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Cena / mes</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Zaduženje</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Status</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Klijenti</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Poslovi</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Datum registracije</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => {
                  // Find company stats from stats.companiesWithStats
                  const companyStats = stats?.companiesWithStats?.find(
                    (cs) => cs._id.toString() === company._id.toString()
                  );
                  
                  return (
                    <tr key={company._id} className="border-b border-gray-700 hover:bg-gray-700/50">
                      <td className="py-3 px-2 text-white">{company.companyName}</td>
                      <td className="py-3 px-2 text-gray-300">{company.email}</td>
                      <td className="py-3 px-2">
                        <span className={`px-2 py-1 rounded text-xs ${packageBadgeClass(company)}`}>
                          {packageLabelSr(company)}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-white font-mono text-xs">
                        {company.billing?.monthlyEur > 0
                          ? `${formatInvoiceEur(company.billing.monthlyEur)} EUR`
                          : '—'}
                      </td>
                      <td className="py-3 px-2 text-white font-mono text-xs">
                        {company.billing?.hasOutstanding ? (
                          <span className="text-amber-300">
                            {formatInvoiceEur(company.billing.outstandingEur)} EUR
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-3 px-2">
                        {company.subscriptionActive ? (
                          <span className="px-2 py-1 bg-green-600 text-white rounded text-xs">Aktivan</span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-600 text-gray-300 rounded text-xs">Neaktivan</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="px-2 py-1 bg-purple-600 text-white rounded text-xs font-semibold">
                          {companyStats?.clientCount || 0}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="px-2 py-1 bg-blue-600 text-white rounded text-xs font-semibold">
                          {companyStats?.totalJobsCount || 0}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-gray-300">
                        {company.createdAt ? new Date(company.createdAt).toLocaleDateString('sr-RS', { 
                          year: 'numeric', 
                          month: '2-digit', 
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 
                        company.created ? new Date(company.created).toLocaleDateString('sr-RS', { 
                          year: 'numeric', 
                          month: '2-digit', 
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </td>
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={async () => {
                            setSelectedCompany(company);
                            setLoadingOrders(true);
                            try {
                              const response = await getCompanyOrders(company._id);
                              setCompanyOrders(response.data);
                            } catch (error) {
                              console.error('Error loading company orders:', error);
                              toast.error(error.response?.data?.message || 'Greška pri učitavanju narudžbina');
                              setCompanyOrders(null);
                            } finally {
                              setLoadingOrders(false);
                            }
                          }}
                          className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                          title="Pregled narudžbina"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={async () => {
                            setViewingClients(company);
                            setLoadingClients(true);
                            setClientsPage(1);
                            try {
                              const response = await getCompanyClients(company._id, 1, 100);
                              setCompanyClients(response.data);
                            } catch (error) {
                              console.error('Error loading company clients:', error);
                              toast.error(error.response?.data?.message || 'Greška pri učitavanju klijenata');
                              setCompanyClients(null);
                            } finally {
                              setLoadingClients(false);
                            }
                          }}
                          className="p-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                          title="Pregled klijenata"
                        >
                          <Users className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingCompany(company)}
                          className="p-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded transition-colors"
                          title="Izmeni kompaniju"
                        >
                          <PenLine className="w-4 h-4" />
                        </button>
                        <button
                          onClick={async () => {
                            setLoadingCompanyDetails(true);
                            try {
                              const response = await getCompanyDetails(company._id);
                              setCompanyDetails(response.data);
                              setDeletingCompany(company);
                            } catch (error) {
                              console.error('Error loading company details:', error);
                              toast.error('Greška pri učitavanju detalja kompanije');
                            } finally {
                              setLoadingCompanyDetails(false);
                            }
                          }}
                          className="p-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                          title="Obriši kompaniju"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Commissions Section */}
        <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mt-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              <h2 className="text-xl font-bold text-white">Provizije od prodaje</h2>
            </div>
            <button
              onClick={() => {
                setShowCommissions(!showCommissions);
                if (!showCommissions) {
                  fetchCommissions();
                }
              }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-sm"
            >
              {showCommissions ? 'Sakrij' : 'Prikaži provizije'}
            </button>
          </div>

          {showCommissions && (
            <div className="space-y-4">
              {/* Commission Stats */}
              {commissionStats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div className="bg-gray-700 rounded-lg p-4">
                    <p className="text-gray-400 text-sm mb-1">Ukupno provizija</p>
                    <p className="text-2xl font-bold text-white">{commissionStats.total || 0}</p>
                  </div>
                  <div className="bg-yellow-900/30 rounded-lg p-4 border border-yellow-600/30">
                    <p className="text-yellow-400 text-sm mb-1">Na čekanju</p>
                    <p className="text-2xl font-bold text-yellow-400">{commissionStats.pending || 0}</p>
                    <p className="text-yellow-300 text-sm mt-1">{formatCurrency(commissionStats.totalPendingAmount || 0)}</p>
                  </div>
                  <div className="bg-green-900/30 rounded-lg p-4 border border-green-600/30">
                    <p className="text-green-400 text-sm mb-1">Plaćeno</p>
                    <p className="text-2xl font-bold text-green-400">{commissionStats.paid || 0}</p>
                    <p className="text-green-300 text-sm mt-1">{formatCurrency(commissionStats.totalPaidAmount || 0)}</p>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <p className="text-gray-400 text-sm mb-1">Ukupan iznos</p>
                    <p className="text-2xl font-bold text-white">{formatCurrency(commissionStats.totalAmount || 0)}</p>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-3">Filteri</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Status</label>
                    <select
                      value={commissionFilters.status}
                      onChange={(e) => setCommissionFilters({ ...commissionFilters, status: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-600 text-white rounded-md border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Svi statusi</option>
                      <option value="pending">Na čekanju</option>
                      <option value="paid">Plaćeno</option>
                      <option value="cancelled">Otkazano</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Kompanija</label>
                    <select
                      value={commissionFilters.companyId}
                      onChange={(e) => setCommissionFilters({ ...commissionFilters, companyId: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-600 text-white rounded-md border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Sve kompanije</option>
                      {companies.map((company) => (
                        <option key={company._id} value={company._id}>
                          {company.companyName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Od datuma</label>
                    <input
                      type="date"
                      value={commissionFilters.startDate}
                      onChange={(e) => setCommissionFilters({ ...commissionFilters, startDate: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-600 text-white rounded-md border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Do datuma</label>
                    <input
                      type="date"
                      value={commissionFilters.endDate}
                      onChange={(e) => setCommissionFilters({ ...commissionFilters, endDate: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-600 text-white rounded-md border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Commissions Table */}
              {commissionsLoading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-blue-500" />
                  <p className="text-gray-400">Učitavanje provizija...</p>
                </div>
              ) : commissions.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Nema provizija za prikaz.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-gray-800">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Kompanija</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Web Shop</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Broj narudžbe</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Datum</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Iznos narudžbe</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Stopa provizije</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Iznos provizije</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Akcije</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {commissions.map((commission) => (
                        <tr key={commission._id} className="hover:bg-gray-700">
                          <td className="px-4 py-2 text-white">
                            {commission.companyId?.companyName || 'N/A'}
                          </td>
                          <td className="px-4 py-2 text-gray-300">
                            {commission.webshopId?.shopName || 'N/A'}
                          </td>
                          <td className="px-4 py-2 text-gray-300">#{commission.orderNumber}</td>
                          <td className="px-4 py-2 text-gray-300">
                            {new Date(commission.createdAt).toLocaleDateString('sr-RS')}
                          </td>
                          <td className="px-4 py-2 text-white">{formatCurrency(commission.orderTotal || 0)}</td>
                          <td className="px-4 py-2 text-gray-300">{commission.commissionRate}%</td>
                          <td className="px-4 py-2 text-green-400 font-semibold">
                            {formatCurrency(commission.commissionAmount || 0)}
                          </td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              commission.status === 'pending' ? 'bg-yellow-600 text-white' :
                              commission.status === 'paid' ? 'bg-green-600 text-white' :
                              'bg-red-600 text-white'
                            }`}>
                              {commission.status === 'pending' ? 'Na čekanju' :
                               commission.status === 'paid' ? 'Plaćeno' :
                               'Otkazano'}
                            </span>
                          </td>
                          <td className="px-4 py-2">
                            {commission.status === 'pending' && (
                              <button
                                onClick={() => setEditingCommission(commission)}
                                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs transition-colors"
                              >
                                Označi kao plaćeno
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Edit Commission Modal */}
        {editingCommission && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-white">Označi proviziju kao plaćenu</h3>
                <button
                  onClick={() => setEditingCommission(null)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-gray-400 text-sm mb-1">Kompanija</p>
                  <p className="text-white">{editingCommission.companyId?.companyName || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Broj narudžbe</p>
                  <p className="text-white">#{editingCommission.orderNumber}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Iznos provizije</p>
                  <p className="text-green-400 font-bold text-lg">{formatCurrency(editingCommission.commissionAmount || 0)}</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Referenca plaćanja (opciono)</label>
                  <input
                    type="text"
                    value={editingCommission.paymentReference || ''}
                    onChange={(e) => setEditingCommission({ ...editingCommission, paymentReference: e.target.value })}
                    placeholder="Broj računa, transakcije, itd."
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Napomena (opciono)</label>
                  <textarea
                    value={editingCommission.notes || ''}
                    onChange={(e) => setEditingCommission({ ...editingCommission, notes: e.target.value })}
                    placeholder="Dodatne napomene..."
                    rows={3}
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <button
                    onClick={() => setEditingCommission(null)}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors"
                  >
                    Otkaži
                  </button>
                  <button
                    onClick={() => handleUpdateCommissionStatus(editingCommission._id, 'paid', editingCommission.paymentReference)}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
                  >
                    Označi kao plaćeno
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sent Notifications */}
        {sentNotifications.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mt-6 border border-gray-700">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="h-5 w-5 text-blue-500" />
              <h2 className="text-xl font-bold text-white">Poslata obaveštenja</h2>
            </div>
            <div className="space-y-3">
              {sentNotifications.slice(0, 5).map((notification) => (
                <div key={notification._id} className="bg-gray-700 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-white font-semibold">{notification.title}</h3>
                      <p className="text-gray-300 text-sm mt-1">{notification.message}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          notification.type === 'info' ? 'bg-blue-600' :
                          notification.type === 'success' ? 'bg-green-600' :
                          notification.type === 'warning' ? 'bg-yellow-600' :
                          'bg-red-600'
                        } text-white`}>
                          {notification.type}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {new Date(notification.createdAt).toLocaleString('sr-RS')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Edit Company Modal */}
        {editingCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-white">Izmeni kompaniju</h3>
                <button
                  onClick={() => setEditingCompany(null)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Naziv kompanije</label>
                  <input
                    type="text"
                    value={editingCompany.companyName || ''}
                    onChange={(e) => setEditingCompany({ ...editingCompany, companyName: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    value={editingCompany.email || ''}
                    onChange={(e) => setEditingCompany({ ...editingCompany, email: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Paket</label>
                  <select
                    value={resolveCompanyPackageNumber(editingCompany)}
                    onChange={(e) =>
                      setEditingCompany({
                        ...editingCompany,
                        package: Number(e.target.value),
                      })
                    }
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={1}>Besplatni</option>
                    <option value={2}>Starter</option>
                    <option value={3}>Standardni</option>
                    <option value={4}>Biznis</option>
                    <option value={5}>Premium</option>
                  </select>
                </div>
                <div>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={editingCompany.subscriptionActive || false}
                      onChange={(e) => setEditingCompany({ ...editingCompany, subscriptionActive: e.target.checked })}
                      className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    />
                    Aktivan
                  </label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <button
                    onClick={() => setEditingCompany(null)}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors"
                  >
                    Otkaži
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await updateCompanyBySuperAdmin({ 
                          companyId: editingCompany._id, 
                          companyData: editingCompany 
                        });
                        toast.success('Kompanija uspešno ažurirana');
                        setEditingCompany(null);
                        fetchData();
                      } catch (error) {
                        console.error('Error updating company:', error);
                        toast.error(error.response?.data?.message || 'Greška pri ažuriranju kompanije');
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                  >
                    Sačuvaj
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Company Confirmation Modal */}
        {deletingCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg max-w-2xl w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-red-500">Potvrda brisanja kompanije</h3>
                <button
                  onClick={() => {
                    setDeletingCompany(null);
                    setCompanyDetails(null);
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {loadingCompanyDetails ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-blue-500" />
                  <p className="text-gray-400">Učitavanje detalja...</p>
                </div>
              ) : companyDetails ? (
                <div className="space-y-4">
                  <div className="bg-red-900/20 border border-red-600 rounded-lg p-4">
                    <p className="text-red-400 font-semibold mb-2">⚠️ Upozorenje: Ova akcija je nepovratna!</p>
                    <p className="text-white text-sm">
                      Brisanjem kompanije <span className="font-bold">{companyDetails.company.companyName}</span> ({companyDetails.company.email}) 
                      biće obrisani svi podaci vezani za ovu kompaniju.
                    </p>
                  </div>

                  <div className="bg-gray-700 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Kompanija</p>
                      <p className="text-white font-semibold">{companyDetails.company.companyName}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Email</p>
                      <p className="text-white">{companyDetails.company.email}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Vlasnik</p>
                      <p className="text-white">{companyDetails.company.ownerName || 'N/A'}</p>
                    </div>
                    <div className="border-t border-gray-600 pt-3 mt-3">
                      <p className="text-gray-400 text-sm mb-2">Statistika:</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-gray-400 text-xs">Broj klijenata</p>
                          <p className="text-white font-bold text-lg">{companyDetails.clientsCount}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-xs">Završeni poslovi</p>
                          <p className="text-white font-bold text-lg">{companyDetails.completedJobsCount}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-xs">Ukupno poslova</p>
                          <p className="text-white font-bold text-lg">{companyDetails.totalJobsCount}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {companyDetails.clientsCount > 0 && (
                    <div className="bg-gray-700 rounded-lg p-4 max-h-60 overflow-y-auto">
                      <p className="text-gray-400 text-sm mb-2">Lista klijenata ({companyDetails.clients.length} od {companyDetails.clientsCount}):</p>
                      <div className="space-y-2">
                        {companyDetails.clients.map((client, index) => {
                          // Format address - handle both string and object formats
                          let addressText = '-';
                          if (client.address) {
                            if (typeof client.address === 'string') {
                              addressText = client.address;
                            } else if (typeof client.address === 'object') {
                              const parts = [];
                              if (client.address.street) parts.push(client.address.street);
                              if (client.address.number) parts.push(client.address.number);
                              if (client.address.floor) parts.push(`Spr. ${client.address.floor}`);
                              if (client.address.apartment) parts.push(`Br. ${client.address.apartment}`);
                              addressText = parts.length > 0 ? parts.join(', ') : '-';
                            }
                          }
                          
                          return (
                            <div key={index} className="bg-gray-600 rounded p-2 text-sm">
                              <p className="text-white font-semibold">{client.name || 'Nepoznat klijent'}</p>
                              {client.email && <p className="text-gray-300 text-xs">{client.email}</p>}
                              {client.phone && <p className="text-gray-300 text-xs">{client.phone}</p>}
                              {addressText !== '-' && <p className="text-gray-300 text-xs">{addressText}</p>}
                            </div>
                          );
                        })}
                        {companyDetails.clientsCount > companyDetails.clients.length && (
                          <p className="text-gray-400 text-xs italic">
                            ... i još {companyDetails.clientsCount - companyDetails.clients.length} klijenata
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-4">
                    <button
                      onClick={() => {
                        setDeletingCompany(null);
                        setCompanyDetails(null);
                      }}
                      className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors"
                    >
                      Otkaži
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await deleteCompanyBySuperAdmin(deletingCompany._id);
                          toast.success('Kompanija uspešno obrisana');
                          setDeletingCompany(null);
                          setCompanyDetails(null);
                          fetchData();
                        } catch (error) {
                          console.error('Error deleting company:', error);
                          toast.error(error.response?.data?.message || 'Greška pri brisanju kompanije');
                        }
                      }}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                    >
                      Obriši kompaniju
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-400">Greška pri učitavanju detalja</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Company Clients Modal */}
        {viewingClients && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-gray-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center z-10">
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    Klijenti - {viewingClients.companyName}
                  </h2>
                  {companyClients && (
                    <p className="text-gray-400 text-sm mt-1">
                      Ukupno klijenata: {companyClients.pagination?.total || 0}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setViewingClients(null);
                    setCompanyClients(null);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6">
                {loadingClients ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-blue-500" />
                    <p className="text-gray-400">Učitavanje klijenata...</p>
                  </div>
                ) : companyClients ? (
                  companyClients.clients && companyClients.clients.length > 0 ? (
                    <div className="space-y-4">
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-gray-800">
                          <thead className="bg-gray-700">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Ime</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Email</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Telefon</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Adresa</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Ocena</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Datum kreiranja</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-700">
                            {companyClients.clients.map((client) => (
                              <tr key={client._id} className="hover:bg-gray-700">
                                <td className="px-4 py-2 text-white">{client.name || 'Nepoznat'}</td>
                                <td className="px-4 py-2 text-gray-300">{client.email || '-'}</td>
                                <td className="px-4 py-2 text-gray-300">{client.phone || '-'}</td>
                                <td className="px-4 py-2 text-gray-300">
                                  {client.address ? (
                                    typeof client.address === 'string' ? client.address : 
                                    `${client.address.street || ''} ${client.address.number || ''}`.trim() || '-'
                                  ) : '-'}
                                </td>
                                <td className="px-4 py-2 text-gray-300">
                                  {client.rating ? (
                                    <span className="px-2 py-1 bg-yellow-600 text-white rounded text-xs">
                                      {client.rating}/10
                                    </span>
                                  ) : '-'}
                                </td>
                                <td className="px-4 py-2 text-gray-300">
                                  {client.createdAt ? new Date(client.createdAt).toLocaleDateString('sr-RS') : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {companyClients.pagination && companyClients.pagination.totalPages > 1 && (
                        <div className="flex items-center justify-between pt-4">
                          <button
                            onClick={async () => {
                              if (clientsPage > 1) {
                                const newPage = clientsPage - 1;
                                setClientsPage(newPage);
                                setLoadingClients(true);
                                try {
                                  const response = await getCompanyClients(viewingClients._id, newPage, 100);
                                  setCompanyClients(response.data);
                                } catch (error) {
                                  console.error('Error loading clients:', error);
                                  toast.error('Greška pri učitavanju klijenata');
                                } finally {
                                  setLoadingClients(false);
                                }
                              }
                            }}
                            disabled={clientsPage === 1}
                            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Prethodna
                          </button>
                          <span className="text-gray-300">
                            Strana {companyClients.pagination.page} od {companyClients.pagination.totalPages}
                          </span>
                          <button
                            onClick={async () => {
                              if (clientsPage < companyClients.pagination.totalPages) {
                                const newPage = clientsPage + 1;
                                setClientsPage(newPage);
                                setLoadingClients(true);
                                try {
                                  const response = await getCompanyClients(viewingClients._id, newPage, 100);
                                  setCompanyClients(response.data);
                                } catch (error) {
                                  console.error('Error loading clients:', error);
                                  toast.error('Greška pri učitavanju klijenata');
                                } finally {
                                  setLoadingClients(false);
                                }
                              }
                            }}
                            disabled={clientsPage >= companyClients.pagination.totalPages}
                            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Sledeća
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>Ova kompanija još nema klijenata.</p>
                    </div>
                  )
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-400">Greška pri učitavanju klijenata</p>
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-4 flex justify-end">
                <button
                  onClick={() => {
                    setViewingClients(null);
                    setCompanyClients(null);
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors"
                >
                  Zatvori
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Company Orders Modal */}
        {selectedCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-gray-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center z-10">
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    Narudžbe - {selectedCompany.companyName}
                  </h2>
                  {companyOrders && (
                    <p className="text-gray-400 text-sm mt-1">
                      Ukupno narudžbina: {companyOrders.totalOrders} | 
                      Ukupan prihod: {formatCurrency(companyOrders.totalRevenue || 0)}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelectedCompany(null);
                    setCompanyOrders(null);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6">
                {loadingOrders ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-blue-500" />
                    <p className="text-gray-400">Učitavanje narudžbina...</p>
                  </div>
                ) : companyOrders ? (
                  companyOrders.webshop ? (
                    <div className="space-y-6">
                      {/* Web Shop Info */}
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-white mb-2">Web Shop informacije</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-400">Naziv shopa:</span>
                            <p className="text-white">{companyOrders.webshop.shopName}</p>
                          </div>
                          <div>
                            <span className="text-gray-400">Slug:</span>
                            <p className="text-white">{companyOrders.webshop.shopSlug}</p>
                          </div>
                        </div>
                      </div>

                      {/* Orders Table */}
                      {companyOrders.orders && companyOrders.orders.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-gray-800">
                            <thead className="bg-gray-700">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Tip</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Broj narudžbe</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Kupac</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Datum</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Paket</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Ukupno</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Način plaćanja</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                              {companyOrders.orders.map((order) => {
                                const customerName = order.customerId?.firstName && order.customerId?.lastName
                                  ? `${order.customerId.firstName} ${order.customerId.lastName}`
                                  : order.customerId?.firstName || 'Nepoznat kupac';
                                
                                const isSubscription = order.type === 'subscription';
                                const statusMap = {
                                  'pending': { label: 'Na čekanju', color: 'bg-yellow-600' },
                                  'processing': { label: 'U obradi', color: 'bg-blue-600' },
                                  'shipped': { label: 'Poslato', color: 'bg-purple-600' },
                                  'delivered': { label: 'Isporučeno', color: 'bg-green-600' },
                                  'paid': { label: 'Plaćeno', color: 'bg-green-600' },
                                  'verified': { label: 'Verifikovano', color: 'bg-green-600' },
                                  'cancelled': { label: 'Otkazano', color: 'bg-red-600' }
                                };
                                
                                const statusInfo = statusMap[order.status] || { label: order.status, color: 'bg-gray-600' };
                                
                                const packageMap = {
                                  'free': 'Besplatni',
                                  'standard': 'Standardni',
                                  'business': 'Biznis',
                                  'premium': 'Premium'
                                };
                                
                                return (
                                  <tr key={order._id} className="hover:bg-gray-700">
                                    <td className="px-4 py-2">
                                      <span className={`px-2 py-1 rounded text-xs ${
                                        isSubscription ? 'bg-blue-600 text-white' : 'bg-purple-600 text-white'
                                      }`}>
                                        {isSubscription ? 'Pretplata' : 'Web Shop'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-white">#{order.orderNumber || order._id.toString().slice(-8)}</td>
                                    <td className="px-4 py-2 text-gray-300">{customerName}</td>
                                    <td className="px-4 py-2 text-gray-300">
                                      {new Date(order.createdAt).toLocaleDateString('sr-RS')}
                                    </td>
                                    <td className="px-4 py-2">
                                      <span className={`px-2 py-1 rounded text-xs text-white ${statusInfo.color}`}>
                                        {statusInfo.label}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-gray-300">
                                      {isSubscription && order.subscriptionPackage ? (
                                        <span className="px-2 py-1 rounded text-xs bg-gray-600 text-gray-300">
                                          {packageMap[order.subscriptionPackage] || order.subscriptionPackage}
                                        </span>
                                      ) : '-'}
                                    </td>
                                    <td className="px-4 py-2 text-gray-300">{formatCurrency(order.total || 0)}</td>
                                    <td className="px-4 py-2 text-gray-300 capitalize">
                                      {isSubscription ? 'Virman' : 
                                       order.paymentMethod === 'cash_on_delivery' ? 'Pouzećem' : 
                                       order.paymentMethod || 'N/A'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-12 text-gray-400">
                          <ShoppingCart className="w-16 h-16 mx-auto mb-4 opacity-50" />
                          <p>Ova kompanija još nema narudžbina.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>Ova kompanija još nema web shop.</p>
                    </div>
                  )
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-400">Greška pri učitavanju narudžbina</p>
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-4 flex justify-end">
                <button
                  onClick={() => {
                    setSelectedCompany(null);
                    setCompanyOrders(null);
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors"
                >
                  Zatvori
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;

